import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";
import type { SessionMessageWithParts } from "schemas/generated/opencode";

import React, { useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { toast } from "@replo/design-system/components/toast/Toast";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { CircleCheckIcon } from "@replo/design-system/icons/animated/CircleCheckIcon";
import { CopyIcon } from "@replo/design-system/icons/animated/CopyIcon";
import { useErrorToast } from "@v2/hooks/useErrorToast";
import { writeHtmlToClipboard } from "@v2/utils/clipboard";
import shouldShowV2Debuggers from "@v2/utils/shouldShowV2Debuggers";
import { ExternalLink, MoreHorizontal } from "lucide-react";

import { markdownToHtml } from "../../utils/markdownToHtml";

interface ChatActionsProps {
  messages?: ChatMessageType[];
  sessionMessages?: SessionMessageWithParts[];
  isChatLoading?: boolean;
  isLastAssistantGroup?: boolean;
}

const buildClipboardPayload = ({
  messages,
  sessionMessages,
}: {
  messages?: ChatMessageType[];
  sessionMessages?: SessionMessageWithParts[];
}) => {
  const htmlParts: string[] = [];

  if (sessionMessages) {
    for (const sessionMessage of sessionMessages) {
      const textParts = sessionMessage.parts
        .map((part) => {
          if (part.partData.type !== "text") {
            return null;
          }
          return part.partData.text;
        })
        .filter((text): text is string => text !== null);

      for (const text of textParts) {
        const html = markdownToHtml(text);
        htmlParts.push(html);
      }
    }
  }

  if (messages) {
    for (const message of messages) {
      const textItems = message.content.filter((item) => item.type === "text");
      for (const item of textItems) {
        const html = markdownToHtml(item.text);
        htmlParts.push(html);
      }
    }
  }

  return {
    html: htmlParts.join(""),
  };
};

export const ChatActions: React.FC<ChatActionsProps> = ({
  messages,
  sessionMessages,
  isChatLoading = false,
  isLastAssistantGroup = false,
}) => {
  const errorToast = useErrorToast();

  const [isCopied, setIsCopied] = useState(false);

  if (isLastAssistantGroup && isChatLoading) {
    return null;
  }

  const handleCopy = async () => {
    try {
      const { html } = buildClipboardPayload({ messages, sessionMessages });

      if (!html) {
        return;
      }

      await writeHtmlToClipboard(html, errorToast);

      setIsCopied(true);
      toast({ title: "Message copied to clipboard" });

      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy message:", error);
    }
  };

  const isDebug = shouldShowV2Debuggers();

  const firstMessageId =
    sessionMessages?.[0]?.message.id ?? messages?.[0]?.id ?? null;

  const messageAdminUrl = firstMessageId
    ? `https://admin.replo.app/dashboard/chat-messages?chatMessageId=${firstMessageId}`
    : null;

  const hasDebugMenuItems = isDebug && Boolean(messageAdminUrl);

  return (
    <div className="flex flex-row gap-1 transition-opacity">
      <Tooltip content="Copy message">
        <Button
          icon
          variant="tertiary"
          size="sm"
          onClick={() => void handleCopy()}
        >
          {isCopied ? (
            <CircleCheckIcon className="text-emerald" />
          ) : (
            <CopyIcon className="text-muted" />
          )}
        </Button>
      </Tooltip>
      {hasDebugMenuItems && (
        <Menu>
          <Tooltip content="More options" triggerAsChild>
            <MenuTrigger
              render={
                <Button icon variant="tertiary" size="sm">
                  <MoreHorizontal />
                </Button>
              }
            />
          </Tooltip>
          <MenuContent align="end">
            {messageAdminUrl && (
              <MenuItem
                label="View Message In Admin"
                startEnhancer={<ExternalLink />}
                onClick={() => {
                  window.open(messageAdminUrl, "_blank");
                }}
              />
            )}
          </MenuContent>
        </Menu>
      )}
    </div>
  );
};
